import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAtom } from 'jotai'
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Article as TextIcon,
  Visibility as PublishIcon,
  VisibilityOff as DraftIcon,
  CheckCircle as ApproveIcon,
  Link as LinkIcon,
} from '@mui/icons-material'
import { attachmentsApi, Attachment, Client } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import {
  canEmailClientAboutUpload,
  describeUploadOutcome,
  idsToAnnounce,
  UploadNoticeStatus,
} from '../../utils/uploadNotice'
import { userAtom } from '../../state/auth'

interface AttachmentsTabProps {
  entityId: string
  entityType: 'client' | 'lead' | 'north_star' | 'linkedin' | 'resume_document'
  title?: string
  emptyStateText?: string
  client?: Client
  onPublishConfirm?: (attachment: Attachment) => void
  hideUpload?: boolean
  fallbackEntityId?: string
  fallbackEntityType?: 'client' | 'lead' | 'north_star' | 'linkedin' | 'resume_document'
  /** Publish/Hide controls for files the client sees by default (the Client
      Folder). Unlike the deliverable tabs, new files start visible and hiding
      one is the exception. */
  visibilityControls?: boolean
  /** Staff Internal Approve beyond North Star, which always has it. */
  internalApproval?: boolean
  /** Files to leave out of the list, because another part of the page owns them
      (the uploaded resume, which has its own slot and publish step). */
  hideIds?: string[]
}

// Deliverables the client signs off on from the portal. LinkedIn content is
// written by staff, so the client is its only approver.
const CLIENT_APPROVABLE_TYPES = new Set(['north_star', 'linkedin'])

const FILE_ICONS: Record<string, typeof FileIcon> = {
  'image/': ImageIcon,
  'application/pdf': PdfIcon,
  'application/msword': DocIcon,
  'application/vnd.openxmlformats-officedocument.wordprocessingml': DocIcon,
  'text/': TextIcon,
}

function getFileIcon(contentType: string) {
  for (const [prefix, Icon] of Object.entries(FILE_ICONS)) {
    if (contentType.startsWith(prefix)) {
      return Icon
    }
  }
  return FileIcon
}

// Content types a browser can render inline in a new tab. Everything else —
// Office docs (.docx/.doc/.xlsx/.pptx), unknown/octet-stream, etc. — must be
// downloaded instead: window.open()'ing a .docx opens a blank tab (the browser
// can't render it) or, on an Office-integrated browser, hands off to Microsoft's
// web viewer which shows a "404 - File or directory not found" page (MWA-272).
const INLINE_VIEWABLE_PREFIXES = ['application/pdf', 'image/', 'text/']

function canPreviewInline(contentType?: string): boolean {
  if (!contentType) return false
  return INLINE_VIEWABLE_PREFIXES.some((prefix) => contentType.startsWith(prefix))
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatRole(role: string): string {
  const roleMap: Record<string, string> = {
    'admin': 'Admin',
    'manager': 'Manager',
    'client_success': 'Client Success',
    'resume_writer': 'Resume Writer',
    'sales': 'Sales',
    'coach': 'Coach',
  }
  return roleMap[role] || 'Staff'
}

function getUrlHostname(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return 'link'
  }
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export default function AttachmentsTab({ entityId, entityType, title, emptyStateText, client: _client, onPublishConfirm, hideUpload, fallbackEntityId, fallbackEntityType, visibilityControls, internalApproval, hideIds }: AttachmentsTabProps) {
  const [user] = useAtom(userAtom)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [description, setDescription] = useState('')
  const [uploadAsDraft, setUploadAsDraft] = useState(!visibilityControls)
  const [emailClientOnUpload, setEmailClientOnUpload] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [attachmentToDelete, setAttachmentToDelete] = useState<Attachment | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [publishing, setPublishing] = useState<string | null>(null)
  const [approving, setApproving] = useState<string | null>(null)
  const [internalApproving, setInternalApproving] = useState<string | null>(null)
  const [confirmApproval, setConfirmApproval] = useState<Attachment | null>(null)
  const { showSuccess, showError, showWarning } = useNotification()
  // A second click while an upload or link save is running must not send a second email.
  const uploadInFlight = useRef(false)
  const linkInFlight = useRef(false)

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState(0)

  // Link dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkName, setLinkName] = useState('')
  const [linkDescription, setLinkDescription] = useState('')
  const [linkAsDraft, setLinkAsDraft] = useState(!visibilityControls)
  const [emailClientOnLink, setEmailClientOnLink] = useState(false)
  const [addingLink, setAddingLink] = useState(false)

  // Check if user is staff or client
  const isStaff = user?.role !== 'client'
  const isClient = user?.role === 'client'
  // Draft controls on the deliverable tabs, plus any tab that opts in with visibilityControls.
  // The Client Folder calls a draft "hidden": its files are not works in progress.
  const showDraftControls = isStaff && (visibilityControls || entityType === 'north_star' || entityType === 'linkedin' || entityType === 'resume_document')
  const draftLabel = visibilityControls ? 'Hidden' : 'Draft'
  const draftNote = visibilityControls ? ' (hidden from client)' : ' as draft (not visible to client)'
  // Only the Client Folder (visibilityControls) can email the client about what staff add.
  const canOfferClientEmail = isStaff && !!visibilityControls

  // Emails the client about files that just saved; the upload itself never fails because of it.
  const emailClientAbout = async (saved: Attachment[]): Promise<UploadNoticeStatus | null> => {
    const ids = idsToAnnounce(saved)
    if (ids.length === 0) return null
    try {
      const result = await attachmentsApi.notifyClientOfUploads(entityId, ids)
      return result.status
    } catch (err) {
      console.error('Failed to email the client about the upload:', err)
      return 'failed'
    }
  }

  const showOutcome = ({ severity, message }: { severity: 'success' | 'warning' | 'error'; message: string }) => {
    if (severity === 'error') showError(message)
    else if (severity === 'warning') showWarning(message)
    else showSuccess(message)
  }

  const shownAttachments = useMemo(
    () => (hideIds?.length ? attachments.filter((a) => !hideIds.includes(a.id)) : attachments),
    [attachments, hideIds]
  )

  // Filter attachments by source type
  const fileAttachments = useMemo(
    () => shownAttachments.filter((a) => a.sourceType !== 'link'),
    [shownAttachments]
  )
  const linkAttachments = useMemo(
    () => shownAttachments.filter((a) => a.sourceType === 'link'),
    [shownAttachments]
  )

  // Show the North Star review prompt only while the client has a published document
  // they can still act on — same condition as the client Approve button below.
  const hasNorthStarToReview =
    isClient &&
    entityType === 'north_star' &&
    fileAttachments.some((a) => !a.isDraft && !a.approved && !a.internalApproved)

  const loadAttachments = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const queries: Promise<Attachment[]>[] = [attachmentsApi.list(entityId, entityType)]
      if (fallbackEntityId) {
        queries.push(attachmentsApi.list(fallbackEntityId, fallbackEntityType))
      }
      const results = await Promise.all(queries)
      const primary = results[0]
      const fallback = results[1] || []
      // Merge and deduplicate (primary wins)
      const seenIds = new Set(primary.map((a) => a.id))
      const merged = [...primary, ...fallback.filter((a) => !seenIds.has(a.id))]
      setAttachments(merged)
    } catch (err) {
      console.error('Failed to load attachments:', err)
      setError('Failed to load attachments')
    } finally {
      setLoading(false)
    }
  }, [entityId, entityType, fallbackEntityId, fallbackEntityType])

  useEffect(() => {
    loadAttachments()
  }, [loadAttachments])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    // Reset the input so the same file(s) can be re-selected later
    event.target.value = ''
    if (files.length === 0) return

    // Check file size (max 10MB each)
    const valid: File[] = []
    const tooBig: string[] = []
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        tooBig.push(file.name)
      } else {
        valid.push(file)
      }
    }
    if (tooBig.length > 0) {
      // MWA-286 — Doc wording, with the offending names kept: this path accepts
      // several files at once, so naming them is the difference between a
      // useful message and a guessing game.
      showError(`The file did not upload. Check the file size is under 10MB and try again: ${tooBig.join(', ')}`)
    }
    if (valid.length === 0) return

    setSelectedFiles(valid)
    setUploadDialogOpen(true)
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || uploadInFlight.current) return
    uploadInFlight.current = true

    try {
      setUploading(true)
      const uploaded: Attachment[] = []
      const failed: string[] = []
      // Upload sequentially so a single failure doesn't abort the rest;
      // each file reuses the shared description / draft setting.
      for (const file of selectedFiles) {
        try {
          const attachment = await attachmentsApi.upload(
            file,
            entityType,
            entityId,
            description || undefined,
            showDraftControls ? uploadAsDraft : undefined
          )
          uploaded.push(attachment)
        } catch (err) {
          console.error('Failed to upload file:', file.name, err)
          failed.push(file.name)
        }
      }

      if (uploaded.length > 0) {
        setAttachments([...uploaded, ...attachments])
      }

      const note = showDraftControls && uploadAsDraft ? draftNote : ''
      const wantsEmail = canOfferClientEmail && emailClientOnUpload && canEmailClientAboutUpload(!uploadAsDraft)
      const notice = wantsEmail && uploaded.length > 0 ? await emailClientAbout(uploaded) : null
      showOutcome(
        describeUploadOutcome({
          savedMessage: uploaded.length === 1 ? `File uploaded${note}` : `${uploaded.length} files uploaded${note}`,
          failedNames: failed,
          notice,
        })
      )

      // Keep the dialog open only if nothing uploaded, so the user can retry.
      if (uploaded.length > 0) {
        handleCloseUploadDialog()
      }
    } finally {
      uploadInFlight.current = false
      setUploading(false)
    }
  }

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false)
    setSelectedFiles([])
    setDescription('')
    setUploadAsDraft(!visibilityControls)
    setEmailClientOnUpload(false)
  }

  const handleAddLink = async () => {
    if (!linkUrl.trim() || !linkName.trim() || linkInFlight.current) return
    linkInFlight.current = true

    try {
      setAddingLink(true)
      const attachment = await attachmentsApi.addLink(
        entityType,
        entityId,
        linkUrl.trim(),
        linkName.trim(),
        linkDescription || undefined,
        showDraftControls ? linkAsDraft : undefined
      )
      setAttachments([attachment, ...attachments])
      const wantsEmail = canOfferClientEmail && emailClientOnLink && canEmailClientAboutUpload(!linkAsDraft)
      const notice = wantsEmail ? await emailClientAbout([attachment]) : null
      showOutcome(
        describeUploadOutcome({
          savedMessage: showDraftControls && linkAsDraft ? `Link added${draftNote}` : 'Link added successfully',
          failedNames: [],
          notice,
        })
      )
      handleCloseLinkDialog()
    } catch (err) {
      console.error('Failed to add link:', err)
      showError('Failed to add link')
    } finally {
      linkInFlight.current = false
      setAddingLink(false)
    }
  }

  const handleCloseLinkDialog = () => {
    setLinkDialogOpen(false)
    setLinkUrl('')
    setLinkName('')
    setLinkDescription('')
    setLinkAsDraft(!visibilityControls)
    setEmailClientOnLink(false)
  }

  const handleTogglePublish = async (attachment: Attachment) => {
    // If publishing and a custom confirm handler is provided, delegate to parent
    if (attachment.isDraft && onPublishConfirm) {
      onPublishConfirm(attachment)
      return
    }

    setPublishing(attachment.id)
    try {
      const updated = attachment.isDraft
        ? await attachmentsApi.publish(attachment.id)
        : await attachmentsApi.unpublish(attachment.id)
      setAttachments(attachments.map((a) => (a.id === attachment.id ? updated : a)))
      showSuccess(updated.isDraft ? 'File hidden from client' : 'File published to client')
    } catch (err) {
      console.error('Failed to toggle publish:', err)
      showError('Failed to update file visibility')
    } finally {
      setPublishing(null)
    }
  }

  const handlePreview = async (attachment: Attachment) => {
    try {
      await attachmentsApi.preview(attachment.id)
    } catch (err) {
      console.error('Failed to preview file:', err)
      showError('Failed to open file')
    }
  }

  const handleDownload = async (attachment: Attachment) => {
    try {
      await attachmentsApi.download(attachment.id)
    } catch (err) {
      console.error('Failed to download file:', err)
      showError('Failed to download file')
    }
  }

  const handleDeleteClick = (attachment: Attachment) => {
    setAttachmentToDelete(attachment)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!attachmentToDelete) return

    try {
      setDeleting(true)
      await attachmentsApi.delete(attachmentToDelete.id)
      setAttachments(attachments.filter((a) => a.id !== attachmentToDelete.id))
      showSuccess(attachmentToDelete.sourceType === 'link' ? 'Link removed successfully' : 'File deleted successfully')
      setDeleteDialogOpen(false)
      setAttachmentToDelete(null)
    } catch (err) {
      console.error('Failed to delete:', err)
      showError('Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  // Show confirmation dialog when approve button clicked
  const handleApproveClick = (attachment: Attachment) => {
    setConfirmApproval(attachment)
  }

  // Proceed with approval after confirmation
  const handleConfirmApprove = async () => {
    if (!confirmApproval) return

    setApproving(confirmApproval.id)
    setConfirmApproval(null) // Close dialog immediately

    try {
      const updated = await attachmentsApi.approve(confirmApproval.id)
      setAttachments(attachments.map((a) => (a.id === confirmApproval.id ? updated : a)))
      showSuccess(
        entityType === 'north_star'
          ? 'North Star approved. Your strategist has been notified and your resume build begins next.'
          : 'Document approved.'
      )
    } catch (err) {
      console.error('Failed to approve document:', err)
      showError('Failed to approve document')
    } finally {
      setApproving(null)
    }
  }

  // Cancel approval
  const handleCancelApprove = () => {
    setConfirmApproval(null)
  }

  // Handle internal approval (staff only)
  const handleInternalApprove = async (attachment: Attachment) => {
    setInternalApproving(attachment.id)
    try {
      const updated = await attachmentsApi.internalApprove(attachment.id)
      setAttachments(attachments.map((a) => (a.id === attachment.id ? updated : a)))
      showSuccess('Document internally approved')
    } catch (err) {
      console.error('Failed to internally approve document:', err)
      showError('Failed to internally approve document')
    } finally {
      setInternalApproving(null)
    }
  }

  const renderAttachmentRow = (attachment: Attachment) => {
    const isLink = attachment.sourceType === 'link'
    const Icon = isLink ? LinkIcon : getFileIcon(attachment.contentType)
    // PDFs/images/text open inline; Word & other Office docs download instead (MWA-272)
    const viewable = !isLink && canPreviewInline(attachment.contentType)
    // A staff sign-off on a Client Folder file is internal, so the portal's
    // Documents list does not show it. The deliverable tabs still do.
    const showApprovedTag =
      (attachment.approved || attachment.internalApproved) &&
      !(isClient && entityType === 'client' && !attachment.approved)

    return (
      <Paper key={attachment.id} variant="outlined" sx={{ mb: 1 }}>
        {/* The row of actions cannot shrink — every button is as wide as its
            label — so in a narrow column it has to drop to its own line rather
            than run off the side of the panel. */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', p: 1.5, gap: 1.5 }}>
          <Icon color="primary" sx={{ flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                {attachment.fileName}
              </Typography>
              <Chip
                label={isLink ? getUrlHostname(attachment.sourceUrl || '') : formatFileSize(attachment.fileSize)}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
              {showDraftControls && attachment.isDraft && (
                <Chip
                  icon={<DraftIcon sx={{ fontSize: 14 }} />}
                  label={draftLabel}
                  size="small"
                  color="warning"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              {showApprovedTag && (
                <Chip
                  icon={<ApproveIcon sx={{ fontSize: 14 }} />}
                  label={`Approved${attachment.internalApprovedByRole ? ` by ${formatRole(attachment.internalApprovedByRole)}` : attachment.approvedBy ? ` by Client` : ''}`}
                  size="small"
                  color="success"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
            {attachment.description && (
              <Typography variant="body2" color="text.secondary">
                {attachment.description}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {/* MWA-235 — hide the uploader's identity from clients; staff keep the "by <name>" attribution */}
              {isLink ? 'Added' : 'Uploaded'}
              {!isClient && ` by ${attachment.uploadedByName || attachment.uploadedBy}`} on{' '}
              {formatDate(attachment.createdAt)}
              {showDraftControls && attachment.isDraft && ' (not visible to client)'}
            </Typography>
          </Box>
          {/* Shrinkable, so that once it has wrapped onto its own line it can
              come down to the panel's width and wrap its own buttons. It still
              cannot go narrower than its widest button, so nothing is clipped. */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            {isClient && CLIENT_APPROVABLE_TYPES.has(entityType) && !attachment.isDraft && !attachment.approved && !attachment.internalApproved && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={approving === attachment.id ? <CircularProgress size={14} /> : <ApproveIcon />}
                onClick={() => handleApproveClick(attachment)}
                disabled={approving === attachment.id}
                sx={{ fontSize: '0.75rem', textTransform: 'none', mr: 0.5 }}
              >
                Approve
              </Button>
            )}
            {/* North Star needs the published document; an opted-in tab can
                approve a hidden file too, since staff review comes before sharing. */}
            {isStaff && (entityType === 'north_star' ? !attachment.isDraft : internalApproval) && !attachment.internalApproved && (
              <Tooltip title={entityType === 'north_star' ? 'Approve document internally before client review' : 'Mark this file as approved by staff'}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={internalApproving === attachment.id ? <CircularProgress size={14} /> : <ApproveIcon />}
                  onClick={() => handleInternalApprove(attachment)}
                  disabled={internalApproving === attachment.id}
                  sx={{ fontSize: '0.75rem', textTransform: 'none', mr: 0.5 }}
                >
                  Internal Approve
                </Button>
              </Tooltip>
            )}
            {showDraftControls && (
              <Tooltip title={attachment.isDraft ? 'Publish to client' : 'Hide from client'}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    publishing === attachment.id ? (
                      <CircularProgress size={16} />
                    ) : attachment.isDraft ? (
                      <PublishIcon />
                    ) : (
                      <DraftIcon />
                    )
                  }
                  onClick={() => handleTogglePublish(attachment)}
                  disabled={publishing === attachment.id}
                  color={attachment.isDraft ? 'success' : 'inherit'}
                  sx={{ fontSize: '0.75rem', textTransform: 'none', mr: 0.5 }}
                >
                  {attachment.isDraft ? 'Publish' : visibilityControls ? 'Hide' : 'Unpublish'}
                </Button>
              </Tooltip>
            )}
            <Tooltip title={isLink || viewable ? 'Open' : 'Download'}>
              <IconButton
                size="small"
                onClick={() => {
                  if (isLink && attachment.sourceUrl) {
                    window.open(attachment.sourceUrl, '_blank', 'noopener,noreferrer')
                  } else if (viewable) {
                    handlePreview(attachment)
                  } else {
                    // Office docs can't render inline — download cleanly instead of
                    // window.open()'ing to a blank tab / Microsoft 404 (MWA-272)
                    handleDownload(attachment)
                  }
                }}
                color="primary"
              >
                {isLink || viewable ? (
                  <OpenIcon fontSize="small" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {!isLink && viewable && (
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  onClick={() => handleDownload(attachment)}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isStaff && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(attachment)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>
    )
  }

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="rounded" height={80} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={48} />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title || 'Files'}</Typography>
      </Box>

      <Tabs
        value={activeSubTab}
        onChange={(_, v) => setActiveSubTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Files${fileAttachments.length ? ` (${fileAttachments.length})` : ''}`} />
        <Tab label={`Links${linkAttachments.length ? ` (${linkAttachments.length})` : ''}`} />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Files sub-tab */}
      {activeSubTab === 0 && (
        <Box>
          {hasNorthStarToReview && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Read this carefully. Your resume, your LinkedIn, and your positioning in the
              market are all built from this document. If something does not feel right, say
              so now. A North Star you have genuinely approved is worth ten times more than
              one you scanned.
            </Alert>
          )}
          {!hideUpload && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                component="label"
                disabled={uploading}
              >
                Upload Files
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.xls,.xlsx,.ppt,.pptx"
                />
              </Button>
            </Box>
          )}

          {fileAttachments.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <FileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                {emptyStateText || 'No files uploaded yet'}
              </Typography>
              {!emptyStateText && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Upload documents, proposals, or other files
                </Typography>
              )}
            </Paper>
          ) : (
            <Box>{fileAttachments.map(renderAttachmentRow)}</Box>
          )}
        </Box>
      )}

      {/* Links sub-tab */}
      {activeSubTab === 1 && (
        <Box>
          {!hideUpload && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={() => setLinkDialogOpen(true)}
                disabled={addingLink}
              >
                Add Link
              </Button>
            </Box>
          )}

          {linkAttachments.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <LinkIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">No links added yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add links to Google Drive, Dropbox, or other external resources
              </Typography>
            </Paper>
          ) : (
            <Box>{linkAttachments.map(renderAttachmentRow)}</Box>
          )}
        </Box>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedFiles.length > 1 ? `Upload ${selectedFiles.length} Files` : 'Upload File'}</DialogTitle>
        <DialogContent>
          {selectedFiles.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {selectedFiles.map((file, i) => (
                <Paper key={`${file.name}-${i}`} variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FileIcon color="primary" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap title={file.name}>
                      {file.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={selectedFiles.length > 1 ? 'Add a description (applied to all files)...' : 'Add a description for this file...'}
          />
          {showDraftControls && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!uploadAsDraft}
                  onChange={(e) => {
                    setUploadAsDraft(!e.target.checked)
                    if (!e.target.checked) setEmailClientOnUpload(false)
                  }}
                />
              }
              label="Publish immediately (visible to client upon upload)"
              sx={{ mt: 2 }}
            />
          )}
          {canOfferClientEmail && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={emailClientOnUpload && canEmailClientAboutUpload(!uploadAsDraft)}
                    onChange={(e) => setEmailClientOnUpload(e.target.checked)}
                    disabled={uploading || !canEmailClientAboutUpload(!uploadAsDraft)}
                  />
                }
                label="Email the client about this upload"
              />
              {!canEmailClientAboutUpload(!uploadAsDraft) && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Hidden files are not announced. Publish immediately to email the client.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : showDraftControls && uploadAsDraft ? (visibilityControls ? 'Upload Hidden' : 'Upload as Draft') : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Link Dialog */}
      <Dialog open={linkDialogOpen} onClose={handleCloseLinkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Link</DialogTitle>
        <DialogContent>
          <TextField
            label="URL"
            fullWidth
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://drive.google.com/..."
            sx={{ mt: 1, mb: 2 }}
            error={!!linkUrl && !isValidUrl(linkUrl)}
            helperText={linkUrl && !isValidUrl(linkUrl) ? 'Please enter a valid URL' : ''}
          />
          <TextField
            label="Display Name"
            fullWidth
            value={linkName}
            onChange={(e) => setLinkName(e.target.value)}
            placeholder="e.g., Resume Draft v2"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={linkDescription}
            onChange={(e) => setLinkDescription(e.target.value)}
            placeholder="Add a description..."
          />
          {showDraftControls && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!linkAsDraft}
                  onChange={(e) => {
                    setLinkAsDraft(!e.target.checked)
                    if (!e.target.checked) setEmailClientOnLink(false)
                  }}
                />
              }
              label="Publish immediately (visible to client upon upload)"
              sx={{ mt: 2 }}
            />
          )}
          {canOfferClientEmail && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={emailClientOnLink && canEmailClientAboutUpload(!linkAsDraft)}
                    onChange={(e) => setEmailClientOnLink(e.target.checked)}
                    disabled={addingLink || !canEmailClientAboutUpload(!linkAsDraft)}
                  />
                }
                label="Email the client about this upload"
              />
              {!canEmailClientAboutUpload(!linkAsDraft) && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Hidden files are not announced. Publish immediately to email the client.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog} disabled={addingLink}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddLink}
            disabled={addingLink || !linkUrl.trim() || !isValidUrl(linkUrl) || !linkName.trim()}
            startIcon={addingLink ? <CircularProgress size={16} /> : <LinkIcon />}
          >
            {addingLink ? 'Adding...' : 'Add Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{attachmentToDelete?.sourceType === 'link' ? 'Remove Link' : 'Delete File'}</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently {attachmentToDelete?.sourceType === 'link' ? 'remove' : 'delete'} &quot;{attachmentToDelete?.fileName}&quot;. It cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : attachmentToDelete?.sourceType === 'link' ? 'Remove' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <Dialog
        open={!!confirmApproval}
        onClose={handleCancelApprove}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{entityType === 'linkedin' ? 'Approve your LinkedIn content?' : 'Confirm Document Approval'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {/* Approving LinkedIn content notifies nobody and moves no stage, so
                its copy promises only what happens: the approval shows on the file. */}
            {entityType === 'linkedin'
              ? 'Approve this if the content is ready to go on your LinkedIn profile. Your team will see it marked as approved. An approval cannot be undone from the portal, so if anything needs to change, tell your strategist before approving.'
              : 'Are you sure you want to approve this document? Once approved, your team will be notified and the next steps in your workflow will begin.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelApprove} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmApprove}
            variant="contained"
            color="primary"
            autoFocus
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
